//we need this library to make this code work inside web worker
import * as cheerio from "cheerio";

import { viteEnv } from "utils/vite-env.util";

import { getImageSize } from "./utils";

const SIGN_BUTTON_PLACEHOLDER = `<button class="sign-button" data-id="sign-button">Click to Add Signature</button>`;

const SALE_PLACEHOLDER_NAMES = [
  "Patient.SelectedInvoice.LineItems",
  "Patient.SelectedInvoice.Devices",
  "Patient.SelectedInvoice.Services",
  "Patient.SelectedInvoice.Totals"
] as const;

const getSalePlaceholderHtml = (mergeTagName: string) => {
  const selector = mergeTagName.replaceAll(" ", "-").replaceAll(".", "-");
  return `<button class="select-invoice-button ${selector}" data-id="select-invoice-button-.${selector}" data-options="">Click to Select Invoice</button>`;
};

const formatResourceUrlLocal = (url?: string, bustCache: boolean = false) => {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) return url;

  let formattedUrl: string;
  if (url.startsWith("/")) {
    formattedUrl = (typeof viteEnv !== "undefined" ? viteEnv.VITE_SUNO_API : "")
      ?.replace("/api", "")
      .concat(url);
  } else {
    formattedUrl = url;
  }

  if (bustCache && formattedUrl) {
    const cacheParam = `${formattedUrl.includes("?") ? "&" : "?"}_cb=`;
    if (!formattedUrl.includes(cacheParam)) {
      formattedUrl += `${cacheParam}${Date.now()}`;
    }
  }

  return formattedUrl;
};

const IMAGE_DOWN_RATIO = 0.8;
const IMAGE_MAX_WIDTH = 1000;
// A4 page width (595pt) minus paddingHorizontal 50pt each side = 495pt usable content width.
// react-pdf treats CSS px as pt, so px values map 1:1 to pt in the rendered PDF.
const PDF_PAGE_CONTENT_WIDTH = 495;

const SPECIAL_CHARS = new Set([
  "ʃ",
  "ʒ",
  "θ",
  "ð",
  "ŋ",
  "ɛ",
  "æ",
  "ʊ",
  "ɔ",
  "Ʃ",
  "Ʒ",
  "Θ",
  "Ð",
  "Ŋ",
  "Ɛ",
  "Æ",
  "Ʊ",
  "Ɔ"
]);

// Unicode characters that should be normalized to ASCII equivalents for PDF rendering
// The Lato font doesn't include glyphs for many of these Unicode variants
const CHAR_NORMALIZATION_MAP: Record<string, string> = {
  // Hyphens and dashes → ASCII hyphen-minus (U+002D)
  "\u2010": "-", // Hyphen
  "\u2011": "-", // Non-breaking Hyphen
  "\u2012": "-", // Figure Dash
  "\u2013": "-", // En Dash
  "\u2014": "-", // Em Dash
  "\u2015": "-", // Horizontal Bar
  "\u2212": "-", // Minus Sign
  "\uFE58": "-", // Small Em Dash
  "\uFE63": "-", // Small Hyphen-Minus
  "\uFF0D": "-", // Fullwidth Hyphen-Minus
  // Slashes → ASCII forward slash (U+002F)
  "\u2044": "/", // Fraction Slash
  "\u2215": "/", // Division Slash
  "\uFF0F": "/", // Fullwidth Solidus
  // Quotes → ASCII equivalents
  "\u2018": "'", // Left Single Quotation Mark
  "\u2019": "'", // Right Single Quotation Mark
  "\u201A": "'", // Single Low-9 Quotation Mark
  "\u201B": "'", // Single High-Reversed-9 Quotation Mark
  "\u2032": "'", // Prime
  "\u201C": '"', // Left Double Quotation Mark
  "\u201D": '"', // Right Double Quotation Mark
  "\u201E": '"', // Double Low-9 Quotation Mark
  "\u201F": '"', // Double High-Reversed-9 Quotation Mark
  "\u2033": '"', // Double Prime
  // Spaces → ASCII space
  "\u00A0": " ", // Non-breaking Space
  "\u2002": " ", // En Space
  "\u2003": " ", // Em Space
  "\u2009": " ", // Thin Space
  "\u200A": " " // Hair Space
};

// Pre-compile regex for character normalization
const CHAR_NORMALIZATION_REGEX = new RegExp(
  `[${Object.keys(CHAR_NORMALIZATION_MAP).join("")}]`,
  "g"
);

// Normalize Unicode characters to ASCII equivalents for PDF font compatibility
const normalizeCharactersForPdf = (text: string): string => {
  return text.replace(
    CHAR_NORMALIZATION_REGEX,
    (char) => CHAR_NORMALIZATION_MAP[char] || char
  );
};

const CHECKED_IMAGE_DATA =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAA8FBMVEUAAABAm/8+lf9Blv8/mP9Bmf9Amf9Amf9Amf8/mf9Amf9Amf8/mf9Amf9Amf9Bmf9Bmv9Cmv9Dm/9FnP9Hnf9Knv9Ln/9NoP9Pof9UpP9VpP9Ypv9Zpv9lrf9mrf9or/9rsP9xs/9ztP90tf93tv94t/98uf99uf99uv+Evf+Hv/+Pw/+QxP+Uxv+Xx/+YyP+gzP+hzf+w1f+x1f+y1v+z1v+42f+52v/C3//F4P/G4f/J4v/L4//N5P/S5//Y6v/b7P/d7f/k8f/n8v/p8//r9P/s9f/u9v/y+P/3+//4+//5/P/6/P/7/f/9/v////9mUtzUAAAADnRSTlMAHB0nqaqsr+zt7vDx8r0xoZ0AAAABYktHRE9uZkFJAAAA8UlEQVQ4y7WTyVICQRBEi2FTZEpQRgVF3HDfd0VFcWFR+/3/33jAMAKcrjmRx+oX0d2ZWSJBLlSPwnxaJJhWQ4VAcmoqK6ENFEUTNDGg8rBjA9dwExnAIdCt+oHGF7hN/xWLr8CJ/w2lFnA3OwrUbqM/4AzoLIx+s9ahNfc72XDwWR/z4Qk4Hw6We8D+uFH1PnCkqjrfBi7+O9l04LZV9Qp4rsRYfQwMGroH9FZis7gEPpoDcFvxYZXvGerUl2b0AsBj2Rt39R14WzL6sNrne80szK47SGjU+kRKm7A4M5K3gYykC9b5VEokyBa9i5lJyQ+0alHb7i7hVgAAAABJRU5ErkJggg==";

const NON_CHECKED_IMAGE_DATA =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAAVklEQVQoke3OoRHAMAwDQCnnMg9l7vB6w6zhocISUNReUZMBKvzSie4eJBv2EkKy9d6PzJxf0sxEVUcBgBV+m7J55clf2C6YmazgbQRAqOqotS7XSZ4XcaAVtq2QwAkAAAAASUVORK5CYII=";

// Pre-compile regex for better performance
const SPECIAL_CHARS_REGEX = new RegExp(
  `(${[...SPECIAL_CHARS].join("|")})`,
  "g"
);

const removePlaceholders = (html: string) => {
  if (html.includes(SIGN_BUTTON_PLACEHOLDER)) {
    html = html.replaceAll(SIGN_BUTTON_PLACEHOLDER, "");
  }
  for (const name of SALE_PLACEHOLDER_NAMES) {
    const placeholder = getSalePlaceholderHtml(name);
    if (html.includes(placeholder)) {
      html = html.replaceAll(placeholder, "");
    }
  }
  if (html.includes(`<button class="select-invoice-button`)) {
    const $ = cheerio.load(html);
    $(`button.select-invoice-button`).remove();
    html = $.html();
  }
  return html;
};

// Simple cache for normalized HTML to avoid recomputing identical inputs
const normalizeCache = new Map<string, string>();
const MAX_CACHE_SIZE = 100; // Limit cache size to prevent memory issues

export const normalizePrintableHtml = (data: string) => {
  // Check cache first
  if (normalizeCache.has(data)) {
    return normalizeCache.get(data)!;
  }

  // Normalize Unicode characters to ASCII equivalents for PDF font compatibility
  // This must be done before cheerio parsing to ensure proper character handling
  const normalizedData = normalizeCharactersForPdf(data);
  const html = removePlaceholders(normalizedData);

  const $ = cheerio.load(html);

  removeFontFamily($);
  // Reset regex lastIndex before test for consistent behavior
  SPECIAL_CHARS_REGEX.lastIndex = 0;
  if (SPECIAL_CHARS_REGEX.test(html)) {
    wrapSpecialCharacters($);
  }
  if (html.includes("table")) {
    adjustTables($);
  }

  if (html.includes("img")) {
    adjustImages($);
  }

  if (html.includes("tox-checklist")) {
    fixCheckboxes($);
  }

  if (html.includes("ul") || html.includes("ol")) {
    adjustLists($);
  }

  const result = $.html();

  // Cache the result with size limit
  if (normalizeCache.size >= MAX_CACHE_SIZE) {
    // Remove oldest entry (simple FIFO)
    const firstKey = normalizeCache.keys().next().value;
    if (firstKey !== undefined) {
      normalizeCache.delete(firstKey);
    }
  }
  normalizeCache.set(data, result);

  return result;
};

const wrapSpecialCharacters = ($: cheerio.Root) => {
  $("body, body *")
    .contents()
    .each((_, el) => {
      if (el.type !== "text") return;

      const node = $(el);
      // Skip protected parent elements
      if (
        node.parents("script, style, textarea, pre, code").length > 0 ||
        node.parents('span[style*="font-family: Tinos"]').length > 0
      ) {
        return;
      }

      const text = el.data || "";
      // Reset regex lastIndex before test for consistent behavior
      SPECIAL_CHARS_REGEX.lastIndex = 0;
      if (!SPECIAL_CHARS_REGEX.test(text)) return;

      // Reset regex lastIndex before replace for consistent behavior
      SPECIAL_CHARS_REGEX.lastIndex = 0;
      const wrappedText = text.replace(
        SPECIAL_CHARS_REGEX,
        (match) => `<span style="font-family: Tinos">${match}</span>`
      );

      node.replaceWith(wrappedText);
    });
};

const NON_NUMERIC_FONT_SIZES = new Set([
  "xx-small",
  "x-small",
  "small",
  "medium",
  "large",
  "x-large",
  "xx-large",
  "xxx-large"
]);

const FONT_SIZE_REGEX = /font-size\s*:\s*([^;]+)/i;

const removeFontFamily = ($: cheerio.Root) => {
  $('[style*="font-family"], [style*="font-size"], [style*="font-style"]').each(
    (_, element) => {
      const currentStyles = $(element).attr("style") ?? "";
      if (!currentStyles) return;

      const newStyles = currentStyles
        .split(";")
        .filter((style) => {
          const trimmedStyle = style.trim();
          if (!trimmedStyle) return false;
          if (trimmedStyle.includes("font-family")) {
            return false;
          }
          if (trimmedStyle.includes("font-style")) {
            return false;
          }
          if (trimmedStyle.includes("font-size")) {
            FONT_SIZE_REGEX.lastIndex = 0;
            const fontSizeMatch = FONT_SIZE_REGEX.exec(trimmedStyle);
            if (fontSizeMatch) {
              const fontSizeValue = fontSizeMatch[1].trim().toLowerCase();
              if (NON_NUMERIC_FONT_SIZES.has(fontSizeValue)) {
                return false;
              }
            }
          }
          return true;
        })
        .join(";");

      if (newStyles !== currentStyles) {
        $(element).attr("style", newStyles);
      }
    }
  );
};

const adjustTables = ($: cheerio.Root) => {
  // Adjust table styles
  $("table").each((_, table) => {
    $(table).attr("style", "border-collapse: collapse;");
  });

  $("tr").each((_, row) => {
    $(row).attr("style", "");
  });

  $("th, td").each((_, cell) => {
    const border = $(cell).attr("border") ?? "1";
    const bgColor = $(cell).attr("bgcolor") ?? "";
    let styles = ($(cell).attr("style") ?? "")
      .split(";")
      .filter(
        (style) => !style.includes("padding") && !style.includes("margin")
      )
      .map((style) => {
        let [name, value] = style.split(":").map((item) => item.trim());
        if (name.startsWith("height")) {
          name = "min-height";
        }
        return [name, value].join(":");
      });

    let innerHtml = $(cell).html() ?? "";
    let newStyles: string[] = [];

    if (
      border !== "0" &&
      !styles.some((style) => style.includes("border-style"))
    ) {
      newStyles.push("border-style: solid");
      newStyles.push("border-collapse: collapse");
      if (!styles.some((style) => style.includes("border-width"))) {
        newStyles.push("border-width: 1px");
      }
      if (!styles.some((style) => style.includes("border-color"))) {
        newStyles.push("border-color: #ccc");
      }
    }

    newStyles.push("padding-left: 6.5px");
    newStyles.push("padding-right: 6.5px");
    newStyles.push("padding-top: 0px");

    if (bgColor) {
      newStyles.push(`background-color: ${bgColor}`);
    }
    innerHtml = `<div style="height: 6px"></div><div>${innerHtml}</div><div style="height: 6px"></div>`;
    newStyles = [...styles, ...newStyles].filter((style) => style !== ":");

    const tagName = $(cell).get(0).tagName;

    // Preserve important table cell attributes
    const preservedAttrs = ["colspan", "rowspan", "scope", "headers"];
    let attributesString = "";
    preservedAttrs.forEach((attr) => {
      const value = $(cell).attr(attr);
      if (value) {
        attributesString += ` ${attr}="${value}"`;
      }
    });

    $(cell).replaceWith(
      `<${tagName} style="${newStyles.join(";")}"${attributesString}>${innerHtml}</${tagName}>`
    );
  });
};

const adjustImages = ($: cheerio.Root) => {
  $("img").each((_, imgElement) => {
    let styles = $(imgElement).attr("style") ?? "";
    let newStyles: string[] = [];

    const alt = ($(imgElement).attr("alt") ?? "").toLowerCase();

    const isSignature = alt.includes("signature");

    const isMergeTag = alt.includes("mergetag");

    if (isSignature) {
      newStyles.push("object-fit: scale-down");
      newStyles.push("object-position-x: 0");
      newStyles.push("object-position-y: 0");
      if (isMergeTag) {
        newStyles.push("width: 350px");
      } else {
        newStyles.push("width: 430px");
      }

      newStyles.push("max-height: 110px");
    }

    // Extract dimensions from inline style BEFORE stripping (TinyMCE stores user-resized values here).
    // Negative lookbehind prevents matching "max-width" or "min-width".
    const styleWidthMatch = /(?<![a-z-])width\s*:\s*([\d.]+)px/i.exec(styles);
    const styleHeightMatch = /(?<![a-z-])height\s*:\s*([\d.]+)px/i.exec(styles);
    const styleWidth = styleWidthMatch ? parseFloat(styleWidthMatch[1]) : 0;
    const styleHeight = styleHeightMatch ? parseFloat(styleHeightMatch[1]) : 0;

    // HTML attribute dimensions (original unresized image size)
    const attrWidth =
      parseFloat(($(imgElement).attr("width") ?? "").replace("px", "")) || 0;
    const attrHeight =
      parseFloat(($(imgElement).attr("height") ?? "").replace("px", "")) || 0;

    // Prefer inline style dimensions (user-resized); fall back to HTML attributes
    const effectiveWidth = styleWidth || attrWidth;
    const effectiveHeight = styleHeight || attrHeight;

    // Filter out width and height styles to avoid conflicts with what we push below
    styles = styles
      .split(";")
      .filter(
        (style) =>
          !style.trim().startsWith("width:") &&
          !style.trim().startsWith("height:")
      )
      .join(";");

    const src = formatResourceUrlLocal($(imgElement).attr("src") ?? "", true);

    if (!isSignature) {
      if (effectiveWidth) {
        if (effectiveWidth > PDF_PAGE_CONTENT_WIDTH) {
          const ratio = PDF_PAGE_CONTENT_WIDTH / effectiveWidth;
          newStyles.push(`width: ${PDF_PAGE_CONTENT_WIDTH}px`);
          if (effectiveHeight) {
            newStyles.push(`height: ${Math.round(effectiveHeight * ratio)}px`);
          }
        } else {
          newStyles.push(`width: ${effectiveWidth}px`);
          if (effectiveHeight) {
            newStyles.push(`height: ${effectiveHeight}px`);
          }
        }
      } else if (!effectiveHeight && src?.includes("data:image")) {
        const size = getImageSize(src);
        if (
          size?.width &&
          size?.height &&
          size.width < IMAGE_MAX_WIDTH &&
          size.height < IMAGE_MAX_WIDTH
        ) {
          $(imgElement).attr("width", String(size.width * IMAGE_DOWN_RATIO));
          $(imgElement).attr("height", String(size.height * IMAGE_DOWN_RATIO));
          newStyles.push(`width: ${size.width * IMAGE_DOWN_RATIO}px`);
          newStyles.push(`height: ${size.height * IMAGE_DOWN_RATIO}px`);
        } else {
          newStyles.push(`max-width: ${IMAGE_MAX_WIDTH}px`);
        }
      } else if (!effectiveWidth) {
        newStyles.push(`max-width: ${IMAGE_MAX_WIDTH}px`);
      }
    }

    const inlineStyle = [styles, ...newStyles].join(";");

    if (src) {
      $(imgElement).attr("src", src);
    }
    $(imgElement).attr("style", inlineStyle);
  });
};

const fixCheckboxes = ($: cheerio.Root) => {
  $("ul.tox-checklist").each((_, ulElement) => {
    const newUl = $("<div></div>").css({
      display: "flex",
      flexDirection: "column",
      gap: "8.25px",
      marginTop: "4px",
      marginBottom: "10px"
    });

    $(ulElement)
      .children()
      .each((_, liElement) => {
        const isChecked = $(liElement).hasClass("tox-checklist--checked");
        const isCentered = ($(liElement).attr("style") ?? "").includes(
          "text-align: center"
        );

        const newLi = $("<div></div>").css({
          display: "flex",
          flexDirection: "row",
          gap: "7.5px",
          paddingLeft: "13.5px",
          justifyContent: isCentered ? "center" : undefined,
          justifySelf: isCentered ? "center" : undefined,
          justifyItems: isCentered ? "center" : undefined
        });

        const img = $("<img>")
          .css({
            width: "14px",
            height: "14px",
            borderRadius: "1px"
          })
          .attr("src", isChecked ? CHECKED_IMAGE_DATA : NON_CHECKED_IMAGE_DATA);

        const innerHtml = $(liElement).html() ?? "";

        const content = $("<div></div>").html(innerHtml).css({
          marginTop: "-4px"
        });

        newLi.append(img).append(content);
        newUl.append(newLi);
      });

    $(ulElement).replaceWith(newUl);
  });
};

const adjustLists = ($: cheerio.Root) => {
  // Process ul and ol elements to allow page breaks
  $("ul, ol").each((_, listElement) => {
    const currentStyle = $(listElement).attr("style") ?? "";
    const styleParts = currentStyle
      .split(";")
      .filter((s) => s.trim())
      .map((s) => s.trim());

    // Remove any existing page-break-inside: avoid and margin: 0 (but preserve margin-bottom if set)
    const filteredStyles = styleParts.filter(
      (style) =>
        !style.includes("page-break-inside") &&
        !style.includes("break-inside") &&
        !style.toLowerCase().includes("page-break") &&
        style.trim() !== "margin: 0" &&
        !style.trim().startsWith("margin: 0 ")
    );

    // Ensure margin-bottom is set if not already present
    const hasMarginBottom = filteredStyles.some((style) =>
      style.includes("margin-bottom")
    );
    if (!hasMarginBottom) {
      filteredStyles.push("margin-bottom: 18px");
    }

    // Add styles to allow page breaks inside lists
    filteredStyles.push("page-break-inside: auto");
    filteredStyles.push("break-inside: auto");

    $(listElement).attr("style", filteredStyles.join("; "));

    // Also ensure parent divs allow breaking
    $(listElement)
      .parents("div")
      .each((_, parentDiv) => {
        const parentStyle = $(parentDiv).attr("style") ?? "";
        if (
          !parentStyle.includes("page-break-inside") &&
          !parentStyle.includes("break-inside")
        ) {
          const parentStyleParts = parentStyle
            .split(";")
            .filter((s) => s.trim())
            .map((s) => s.trim());
          parentStyleParts.push("page-break-inside: auto");
          parentStyleParts.push("break-inside: auto");
          $(parentDiv).attr("style", parentStyleParts.join("; "));
        }
      });
  });

  // Process li elements to allow page breaks
  $("li").each((_, liElement) => {
    const currentStyle = $(liElement).attr("style") ?? "";
    const styleParts = currentStyle
      .split(";")
      .filter((s) => s.trim())
      .map((s) => s.trim());

    // Remove any existing page-break properties
    const filteredStyles = styleParts.filter(
      (style) =>
        !style.includes("page-break-inside") &&
        !style.includes("page-break-after") &&
        !style.includes("break-inside") &&
        !style.includes("break-after") &&
        !style.toLowerCase().includes("page-break")
    );

    // Add styles to allow page breaks
    filteredStyles.push("page-break-inside: auto");
    filteredStyles.push("break-inside: auto");
    filteredStyles.push("page-break-after: auto");
    filteredStyles.push("break-after: auto");

    $(liElement).attr("style", filteredStyles.join("; "));
  });
};

const TEXT_TAGS = "div, span, p, h1, h2, h3, i, b, strong, u, em";
const CONTAINER_TAGS = ["div", "span", "p"];
const nbspRegex = /^(&nbsp;|\s*)$/i;

function isEmptyContainer($el: any): boolean {
  return (
    Object.keys($el.attr() || {}).length === 0 && $el.children().length === 1
  );
}

function attrsEqual($a: any, $b: any): boolean {
  const aAttrs = $a.attr() || {};
  const bAttrs = $b.attr() || {};

  if (Object.keys(aAttrs).length !== Object.keys(bAttrs).length) {
    return false;
  }

  for (const key in aAttrs) {
    if (aAttrs[key] !== bAttrs[key]) {
      return false;
    }
  }

  return true;
}

const removeEmptyContainers = ($: any, position: string) => {
  let modified;
  do {
    modified = false;
    const elements = $(CONTAINER_TAGS.join(",")).get();
    if (elements.length === 0) break;

    let indexToCheck = -1,
      shouldRemove = false;

    if (position === "top") {
      indexToCheck = 1; //Check the first element only
      const element = $(elements[indexToCheck]);

      if (element) {
        const currentContent = element.html()?.trim();
        shouldRemove = currentContent ? nbspRegex.test(currentContent) : false;
      }
    } else {
      // 'bottom'
      indexToCheck = elements.length - 1; //Check the last element only
      const element = $(elements[indexToCheck]);
      const currentContent = element.html()?.trim();
      shouldRemove = currentContent ? nbspRegex.test(currentContent) : false;
    }

    if (shouldRemove) {
      $(elements[indexToCheck]).remove();
      modified = true;
    }
  } while (modified);
};

export function cleanHtmlForPrint(html: string): string {
  const normalizedHtml = normalizeCharactersForPdf(html);
  const loadFn = cheerio.load as any;
  const $ = loadFn(normalizedHtml, null, false);

  //Remove unnecessary nested containers
  let modified;
  do {
    modified = false;
    const elements = $(CONTAINER_TAGS.join(",")).get();

    for (let i = elements.length - 1; i >= 0; i--) {
      const $element = $(elements[i]);
      const $parent = $element.parent();

      if (
        $parent.length &&
        CONTAINER_TAGS.includes($parent[0].tagName.toLowerCase()) &&
        isEmptyContainer($parent)
      ) {
        $element.insertBefore($parent);
        $parent.remove();
        modified = true;
        break;
      }
    }
  } while (modified);

  //Remove duplicate empty siblings
  $(TEXT_TAGS).each((_: any, element: any) => {
    const $current = $(element);
    const htmlContent = $current.html()?.trim();

    if (htmlContent && nbspRegex.test(htmlContent)) {
      const $prev = $current.prev();
      if (
        $prev.length &&
        $prev[0].tagName === element.tagName &&
        $prev.html()?.trim() === htmlContent &&
        attrsEqual($prev, $current)
      ) {
        $current.remove();
      }
    }
  });

  removeEmptyContainers($, "top");
  removeEmptyContainers($, "bottom");

  return $.html();
}

export const DEFAULT_PDF_CSS_STYLE = `
body {
  margin: 0px;
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  color: #000000;
}
 div {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  color: #000000;
}

p {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 0px;
  color: #000000;
}

ul {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  page-break-inside: auto !important;
  break-inside: auto !important;
  display: block;
  margin: 0;
  margin-top: 6px;
  margin-bottom: 18px;
  padding: 0;
  color: #000000;
}

li {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  margin-right: 33px;

  page-break-inside: auto !important;
  break-inside: auto !important;
  page-break-after: auto !important;
  break-after: auto !important;
  orphans: 1;
  widows: 1;
  display: list-item;
  color: #000000;
}

ol {
  font-family: LatoRegular;
  line-height: 1.4;
  font-size: 14px;
  page-break-inside: auto !important;
  break-inside: auto !important;
  display: block;
  margin: 0;
  margin-top: 6px;
  margin-bottom: 18px;
  padding: 0;
  color: #000000;
}

table {
  border-collapse: collapse;
  border-spacing: 0;
  color: #000000;
}

h1 {
  font-size: 25px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h2 {
  font-size: 20px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h3 {
  font-size: 16px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h4 {
  font-size: 14px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h5 {
  font-size: 12px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

h6 {
  font-size: 10px;
  margin-top: 12px;
  margin-bottom: 9px;
  color: #000000;
}

figure {
  display: table;
  margin: 16px auto;
}
figure figcaption {
  color: #999;
  display: block;
  margin-top: 4px;
  text-align: center;
}
hr {
  border-color: #333333;
  border-style: solid;
  border-width: 1px 0 0 0;
}
code {
  background-color: #e8e8e8;
  border-radius: 3px;
  padding: 1.6px 3.2px;
}
`;
